import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, ReactNode } from 'react';
import { useAuth } from './auth-context';
import { apiFetch } from './api-client';
import { canAccessMasterDashboard } from './roles';

const STORAGE_KEY = 'active_workspace_id';

export interface WorkspaceOption {
  id: string;
  name: string;
  leaderboardEnabled: boolean;
}

interface WorkspaceContextType {
  workspaceId: string | null;
  workspaceName: string | null;
  /** False for DarwinX admins until a workspace id is resolved (required for API calls). */
  isWorkspaceReady: boolean;
  setWorkspaceId: (id: string) => void;
  workspaces: WorkspaceOption[];
  refreshWorkspaces: () => Promise<void>;
  workspaceHeaders: Record<string, string>;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

let workspaceHeadersGetter: () => Record<string, string> = () => ({});

export function getActiveWorkspaceHeaders(): Record<string, string> {
  return workspaceHeadersGetter();
}

export const WorkspaceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const isDxAdmin = canAccessMasterDashboard(user);
  const [workspaces, setWorkspaces] = useState<WorkspaceOption[]>([]);
  const [workspaceId, setWorkspaceIdState] = useState<string | null>(() => {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(STORAGE_KEY);
  });

  const refreshWorkspaces = useCallback(async () => {
    if (!user || !isDxAdmin) {
      setWorkspaces([]);
      return;
    }
    try {
      const response = await apiFetch('/api/auth/admin/enterprises');
      const data = await response.json();
      if (response.ok && Array.isArray(data)) {
        const options = data.map((entry: { id: string; name: string; leaderboard_enabled?: unknown }) => ({
          id: String(entry.id),
          name: String(entry.name),
          leaderboardEnabled: entry.leaderboard_enabled === 1 || entry.leaderboard_enabled === true || entry.leaderboard_enabled === '1',
        }));
        setWorkspaces(options);
      }
    } catch (error) {
      console.error('Failed to load workspaces', error);
    }
  }, [user, isDxAdmin]);

  useEffect(() => {
    if (!user) {
      setWorkspaceIdState(null);
      setWorkspaces([]);
      return;
    }

    if (!isDxAdmin) {
      setWorkspaceIdState(user.enterprise_id ?? null);
      return;
    }

    void refreshWorkspaces();
  }, [user, isDxAdmin, refreshWorkspaces]);

  useEffect(() => {
    if (!isDxAdmin || workspaces.length === 0) return;

    const saved = localStorage.getItem(STORAGE_KEY);
    const validSaved = saved && workspaces.some((workspace) => workspace.id === saved);
    if (validSaved) {
      setWorkspaceIdState(saved);
      return;
    }

    const nextId = workspaces[0].id;
    setWorkspaceIdState(nextId);
    localStorage.setItem(STORAGE_KEY, nextId);
  }, [isDxAdmin, workspaces]);

  const setWorkspaceId = useCallback((id: string) => {
    setWorkspaceIdState(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const workspaceName = useMemo(() => {
    if (isDxAdmin) {
      return workspaces.find((workspace) => workspace.id === workspaceId)?.name ?? null;
    }
    return user?.enterprise_name ?? null;
  }, [isDxAdmin, workspaces, workspaceId, user?.enterprise_name]);

  const workspaceHeaders = useMemo(() => {
    if (!isDxAdmin || !workspaceId) return {};
    return { 'X-Workspace-Id': workspaceId };
  }, [isDxAdmin, workspaceId]);

  const isWorkspaceReady = !isDxAdmin || workspaceId !== null;

  // Sync during render so child useEffects (which run before this provider's effects) see current headers.
  workspaceHeadersGetter = () => workspaceHeaders;

  const value = useMemo(
    () => ({
      workspaceId: isDxAdmin ? workspaceId : (user?.enterprise_id ?? null),
      workspaceName,
      isWorkspaceReady,
      setWorkspaceId,
      workspaces,
      refreshWorkspaces,
      workspaceHeaders,
    }),
    [isDxAdmin, workspaceId, workspaceName, isWorkspaceReady, setWorkspaceId, workspaces, refreshWorkspaces, workspaceHeaders, user?.enterprise_id]
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export function useWorkspace(): WorkspaceContextType {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}

export async function workspaceFetch(
  input: RequestInfo | URL,
  init: RequestInit = {},
  workspaceHeaders: Record<string, string> = {}
): Promise<Response> {
  const headers = new Headers(init.headers);
  for (const [key, value] of Object.entries(workspaceHeaders)) {
    headers.set(key, value);
  }
  return apiFetch(input, { ...init, headers });
}
